// The 13-week cash-runway chart: the Opex + Vault pool as a solid line against
// the one-month buffer floor, with the Profit failsafe drawn as a lighter
// parallel band above it (break-glass depth, not part of the buffer). The low
// point is marked in rose.

import { useMemo } from 'react';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmt = (n) => '$' + Math.round(n).toLocaleString('en-US');

export default function RunwayChart({ daily, low, buffer, failsafe = 0 }) {
  const c = useMemo(() => {
    const W = 760, H = 264, L = 54, R = 16, T = 16, B = 38;
    const n = daily.length;
    const pool = daily.map((d) => d.pool);
    const withFail = pool.map((v) => v + failsafe);
    let lo = Math.min(...pool, buffer);
    let hi = Math.max(...withFail, buffer);
    const pad = (hi - lo) * 0.12 || 1000; lo -= pad; hi += pad;
    const X = (i) => L + (i / (n - 1)) * (W - L - R);
    const Y = (v) => T + (1 - (v - lo) / (hi - lo)) * (H - T - B);
    const poolPts = daily.map((d, i) => `${X(i).toFixed(1)},${Y(d.pool).toFixed(1)}`).join(' ');
    const area = `${X(0).toFixed(1)},${Y(lo).toFixed(1)} ${poolPts} ${X(n - 1).toFixed(1)},${Y(lo).toFixed(1)}`;
    // failsafe band: from the pool line up to pool + failsafe
    const bandTop = daily.map((d, i) => `${X(i).toFixed(1)},${Y(d.pool + failsafe).toFixed(1)}`);
    const band = `${poolPts} ${bandTop.slice().reverse().join(' ')}`;
    const grid = [];
    for (let s = 0; s <= 4; s++) { const v = lo + ((hi - lo) * s) / 4; grid.push({ y: Y(v), label: '$' + Math.round(v / 1000) + 'k' }); }
    const xl = [];
    for (let i = 0; i < n; i += 14) { const d = daily[i]; xl.push({ x: X(i), label: i === 0 ? 'now' : `${MONTHS[d.date.getMonth()]} ${d.date.getDate()}` }); }
    const li = daily.findIndex((d) => d.pool === low.pool);
    const lx = X(li < 0 ? 0 : li), ly = Y(low.pool);
    return {
      W, H, L, R, poolPts, area, band,
      grid, xl, by: Y(buffer),
      lx, ly, labelY: ly > H - B - 26 ? ly - 9 : ly + 18,
      hasFail: failsafe > 0, failTop: bandTop.join(' '),
    };
  }, [daily, low, buffer, failsafe]);

  return (
    <svg viewBox={`0 0 ${c.W} ${c.H}`} className="block w-full h-auto" role="img" aria-label="13-week cash runway">
      {c.grid.map((g, i) => (
        <g key={i}>
          <line x1={c.L} y1={g.y} x2={c.W - c.R} y2={g.y} stroke="#eef0f4" strokeWidth="1" />
          <text x={c.L - 8} y={g.y + 3.5} textAnchor="end" fontSize="9.5" fill="#8f95a8">{g.label}</text>
        </g>
      ))}
      {c.hasFail && <polygon points={c.band} fill="rgba(10,125,79,.08)" />}
      <polygon points={c.area} fill="rgba(37,150,190,.12)" />
      <polyline points={c.poolPts} fill="none" stroke="#2596BE" strokeWidth="2.5" strokeLinejoin="round" />
      {c.hasFail && <polyline points={c.failTop} fill="none" stroke="#0a7d4f" strokeWidth="1.2" strokeDasharray="4 3" opacity="0.7" />}
      <line x1={c.L} y1={c.by} x2={c.W - c.R} y2={c.by} stroke="#022889" strokeWidth="1.5" strokeDasharray="5 4" opacity="0.85" />
      <text x={c.W - c.R} y={c.by - 6} textAnchor="end" fontSize="9.5" fill="#022889" fontWeight="700">buffer {fmt(buffer)}</text>
      {c.hasFail && <text x={c.L + 2} y={c.grid[0].y + 12} fontSize="9" fill="#0a7d4f" fontWeight="700">+ Profit failsafe</text>}
      <circle cx={c.lx} cy={c.ly} r="4.5" fill="#FF6E83" stroke="#fff" strokeWidth="1.5" />
      <text x={c.lx} y={c.labelY} textAnchor="middle" fontSize="10" fill="#FF6E83" fontWeight="800">low {fmt(low.pool)}</text>
      {c.xl.map((t, i) => <text key={i} x={t.x} y={c.H - 12} textAnchor="middle" fontSize="9" fill="#8f95a8">{t.label}</text>)}
    </svg>
  );
}
